import React, { useContext, useState, useRef } from "react";
import styled from "styled-components";
import { motion, useMotionValue } from "framer-motion";

import useSound from "use-sound";
import { MetricsContext, metricConstants } from "../tome/MetricsContext";
import { TomeContext } from "../tome/TomeContext";
import { transitions } from "../ds/Transitions";

import tile_resize_sound from "../../../sounds/click_01.mp3";
import tile_resize_start_sound from "../../../sounds/action_01.mp3";

const ResizeHandle = styled(motion.div)`
	position: absolute;
	top: 0;
	pointer-events: auto;
	z-index: 9999;
`;

const ResizeHandleMaterial = styled(motion.div)`
	position: absolute;
	left: 50%;
	top: 50%;
	overflow: hidden;
`;

const Indicator = styled(motion.div)`
	position: absolute;
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	padding: 8px;
	gap: 6px;
	border-radius: 8px;
	min-width: 40px;
	text-align: center;

	font-family: "Inter";
	font-style: normal;
	font-weight: 400;
	font-size: 15px;
	line-height: 20px;
`;

const IconLabelGroup = styled(motion.div)`
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: 0px 4px;
`;

export const RowResizeHandle = props => {
	const { pageMargin, minPageHeight, rowHeight, rowMargin, rowMinHeight, rowCount, scale, tileCornerRadius } =
		useContext(MetricsContext).metrics;
	const {
		setTomeData,
		tomeData,
		setRowResizing,
		rowResizing,
		isTileAnimating,
		isPlayMode,
		setShowContextMenu,
		setContextMenuInfo,
	} = useContext(TomeContext);

	const [handleDown, setHandleDown] = useState(false);
	const [handleHovered, setHandleHovered] = useState(false);
	const [handlePanning, setHandlePanning] = useState(false);
	const handleRef = useRef(null);

	/*
	Colors
	*/
	const colors = {};
	colors.handle = props.page.theme.colors.controls.resize.handle;
	colors.handleActive = props.page.theme.colors.controls.resize.handleActive;
	colors.handleWarning = props.page.theme.colors.controls.resize.handleWarning;
	colors.indicatorBackground = props.page.theme.colors.controls.resize.indicatorBackground;
	colors.indicatorForeground = props.page.theme.colors.controls.resize.indicatorForeground;
	colors.indicatorWarning = props.page.theme.colors.controls.resize.indicatorWarning;
	const handleColor = useMotionValue(colors.handle);
	const warningMinHeightColor = useMotionValue(colors.indicatorForeground);

	const warningMinWidthOpacity = useMotionValue(0);

	//const handleSize = metricConstants.cTileResizeHandleSize * scale;
	const handleSize = (rowMargin / 2) - 2;

	const [playSound] = useSound(tile_resize_sound);
	const [playStartSound] = useSound(tile_resize_start_sound);

	const mouseY = useMotionValue(0);
	const indicatorX = useMotionValue(0);

	let handleTop = rowHeight * props.row.height + rowMargin * props.row.height + props.pageTop - 10;

	const indicatorRef = useRef(null);

	let matchingSize = false;
	if (
		rowResizing &&
		rowResizing.id !== props.row.id &&
		rowResizing.height === props.row.height &&
		rowResizing.isResizingHeight
	) {
		matchingSize = true;
		indicatorX.set(rowResizing.indicatorX);
		warningMinHeightColor.set(colors.indicatorForeground);
		//handleColor.set(colors.handleWarning)
	}

	if (props.row.order !== 1) {
		props.rows.forEach(r => {
			// Find all the rows with orders less than this row
			// add up their heights
			if (r.order < props.row.order) {
				handleTop += r.height === 0 ? minPageHeight - pageMargin * 1 : rowHeight * r.height + rowMargin * r.height;
			}
		});
	}

	const updateCursor = rowHeight => {
		if (props.tiles.length === 1) {
			if (rowHeight === props.tiles[0].height12) {
				document.body.style.cursor = "s-resize";
			} else {
				document.body.style.cursor = "ns-resize";
			}
		}

		if (props.tiles.length === 2) {
			if (rowHeight === props.tiles[0].height6 || rowHeight === props.tiles[1].height6) {
				document.body.style.cursor = "s-resize";
			} else {
				document.body.style.cursor = "ns-resize";
			}
		}
	};

	return (
		<ResizeHandle
			ref={handleRef}
			onHoverStart={
				isTileAnimating || isPlayMode || rowResizing
					? null
					: (event, info) => {
							updateCursor(props.row.height);
							handleColor.set(colors.handle);
							setHandleHovered(true);
							warningMinHeightColor.set(colors.indicatorForeground);
							warningMinWidthOpacity.set(0);
					  }
			}
			onHoverEnd={(event, info) => {
				if (!handlePanning) document.body.style.cursor = "auto";
				setHandleHovered(false);
			}}
			onPanStart={(event, info) => {
				if (isPlayMode) return false;
				setHandlePanning(true);
				document.body.classList.add("nocursor");
			}}
			onMouseDown={e => {
				setHandleDown(true);
				if (isPlayMode) return false;
				props.row.isResizingHeight = true;
				setRowResizing(props.row);
				playStartSound();
				const rect = handleRef.current.getBoundingClientRect();
				const newX = e.clientX - rect.x - 20;
				indicatorX.set(newX);
				props.row.indicatorX = newX;

				handleColor.set(colors.handleActive);
				document.body.classList.add("nocursor");
				document.documentElement.classList.add("noscroll");
				//if (selectedTile) setSelectedTile(null);
			}}
			onMouseUp={e => {
				setHandleDown(false);
				props.row.isResizingHeight = undefined;
				handleColor.set(colors.handle);
				setRowResizing(null);
				document.body.classList.remove("nocursor");
				document.documentElement.classList.remove("noscroll");
				warningMinHeightColor.set(colors.indicatorForeground);
				warningMinWidthOpacity.set(0);
			}}
			onPanEnd={(event, info) => {
				setHandleDown(false);
				document.body.style.cursor = "auto";
				props.row.isResizingHeight = undefined;
				setHandlePanning(false);
				setHandleHovered(false);
				setRowResizing(null);
				props.row.isResizingHeight = undefined;
				handleColor.set(colors.handle);
				document.body.classList.remove("nocursor");
				document.documentElement.classList.remove("noscroll");
			}}
			onPan={(event, info) => {
				if (isPlayMode) return false;

				mouseY.set(info.point.y);
				// for this row, find the gridpoint of the pointer position
				let y = info.point.y - props.pageTop;
				let totalRowHeights = 0;
				props.rows.forEach(r => {
					if (r.order < props.row.order) {
						y -= rowHeight * r.height + rowMargin * r.height;
					}
				});
				props.rows.forEach(r => {
					if (r.id !== props.row.id) {
						totalRowHeights += r.height;
					}
				});

				// Find current row unit height
				let gridPoint = Math.round(y / (rowHeight + rowMargin));
				//console.log(totalRowHeights, gridPoint, totalRowHeights + gridPoint);

				// Check if row is tall enough
				if (gridPoint < rowMinHeight) gridPoint = rowMinHeight;
				if (totalRowHeights + gridPoint < rowCount || gridPoint === rowMinHeight) {
					warningMinWidthOpacity.set(1);
					handleColor.set(colors.handleWarning);
					warningMinHeightColor.set(colors.indicatorWarning);
					return false;
				}

				if (props.row.height !== gridPoint) {
					// Stop resizing if the tile content height is greater than row height
					if (props.tiles[0] && props.tiles[0].contentUnitHeights) {
						if (props.tiles[0].contentUnitHeights[props.tiles[0].width] > gridPoint) {
							warningMinWidthOpacity.set(1);
							handleColor.set(colors.handleWarning);
							warningMinHeightColor.set(colors.indicatorWarning);
							return false;
						}
					}
					if (props.tiles[1] && props.tiles[1].contentUnitHeights) {
						if (props.tiles[1].contentUnitHeights[props.tiles[1].width] > gridPoint) {
							warningMinWidthOpacity.set(1);
							handleColor.set(colors.handleWarning);
							warningMinHeightColor.set(colors.indicatorWarning);
							return false;
						}
					}

					// Update row height
					handleColor.set(colors.handleActive);
					warningMinHeightColor.set(colors.indicatorForeground);
					warningMinWidthOpacity.set(0);
					updateCursor(gridPoint);
					props.row.height = gridPoint;
					setTomeData({ ...tomeData });
					playSound();
				}
			}}
			transition={transitions.layoutTransition}
			animate={{
				y: handleTop,
			}}
			style={{
				height: rowMargin + 20,
				right: pageMargin,
				left: pageMargin,
				cursor: handleHovered || handlePanning ? "ns-resize" : "auto",
			}}
			className={"handle"}
			initial={false}
			onContextMenu={e => {
				//console.log("right click!", e);
				setContextMenuInfo({
					x: e.clientX,
					y: e.clientY,
					items: ["Paste"],
				});
				setShowContextMenu(true);
				e.preventDefault();
			}}
		>
			<ResizeHandleMaterial
				animate={{
					opacity: handleHovered || handlePanning || handleDown || matchingSize ? 1 : 0,
				}}
				initial={false}
				transition={transitions.layoutTransition}
				style={{
					height: handleSize,
					width: "100%", //`calc(100% - ${tileCornerRadius * 2}px)`,
					borderRadius: handleSize / 2,
					left: 0,
					x: 0, //tileCornerRadius,
					y: "-50%",
					backgroundColor: handleColor,
				}}
			/>

			<Indicator
				ref={indicatorRef}
				initial={false}
				animate={{
					opacity: handleDown ? 1 : 0,
					scale: handleDown ? 1 : 0,
				}}
				transition={handleDown ? transitions.basic : transitions.instant}
				style={{
					background: colors.indicatorBackground,
					boxShadow: props.page.theme.shadows.small,
					//backdropFilter: "blur(50px)",
					top: "50%",
					y: -18,
					x: indicatorX,
				}}
			>
				<IconLabelGroup
					style={{
						color: warningMinHeightColor,
						//opacity: matchingSize ? 0.4 : 1,
					}}
				>
					{props.row.height}
				</IconLabelGroup>
			</Indicator>
		</ResizeHandle>
	);
};
